import logger from "../services/Logger";
import PromptStore from "../store/promptStore";
import QUERYKEYS, { chatModelInfoQueryKey } from "./queryKeys";
import { v4 as uuidv4 } from "uuid";
import { FileStore } from "../store/fileUploadStore";
import useHeaderSelectedAIModel from "../store/useHeaderSelectedAIModel";
import useModelStore from "../store/useModelStore";
import useGenerateApiSignal from "../store/useGenerateApiSignal";
import { setChatModel } from "./queryFunctions";
import { 
  updateGenerateMessage,
  updateMessageModelId,
  addGenerateErrorMessage,
  attachRecoveryStream,
  updateAnswerUsage,
  runMessageStream,
} from "../utils/streamingUtils";
import { generateMessageApi } from "./generate/messageOperations";
import { createMessageLoadingHandlers } from "./generate/useMessageLoading";
import { getFilesModelCompatibilityError } from "../utils/fileModelCompatibility";
import useFeedbackStore from "../store/useFeedbackStore";

const LINEAGE_ERROR_CODES = new Set([
  "incomplete_prompt_lineage",
  "invalid_prompt_lineage",
  "missing_prompt_lineage",
  "stale_prompt_lineage",
  "spark_access_denied",
  "duplicate_prompt_immutable",
]);

export const resolveSubmissionLineage = (messages, selectedAnswerByPrompt) => {
  if (!messages?.length) return { previousPromptId: null, parentAnswerId: null };
  const previousPrompt = messages[messages.length - 1];
  const parentAnswerId = selectedAnswerByPrompt?.[previousPrompt?.id];
  const selectedAnswer = previousPrompt?.answers?.find(
    (answer) => answer?.id === parentAnswerId && !answer.awaiting_approval
  );
  if (!previousPrompt?.id || !parentAnswerId || !selectedAnswer) {
    return { error: "The selected response changed. Review the visible response and try again." };
  }
  return {
    previousPromptId: selectedAnswer.prompt_id || previousPrompt.id,
    parentAnswerId,
  };
};

export const handleSend = async ({
  chat_id,
  sendMessageMutation,
  messages,
  setRegenerateIndex,
  setIsLatestMessageLoading,
  setAddChatDisabled,
  user,
  orgId,
  files,
  modeRef,
  apiUrl,
  setGenerateStatus,
  space,
  agent,
  chat,
  onApprovalCard,
  clearAllApprovals,
  queryClient,
  deviceId = null,
}) => {
  const lineage = resolveSubmissionLineage(
    messages,
    PromptStore.getState().selectedAnswerByPrompt
  );
  if (lineage.error) {
    useFeedbackStore.getState().showFeedback(lineage.error, "error");
    return false;
  }
  const selectedModel = useHeaderSelectedAIModel.getState().selectedModel;
  const explicitModelId = useModelStore.getState().selectedModels[chat_id] || null;
  const compatibilityError = getFilesModelCompatibilityError(
    FileStore.getState().files,
    selectedModel
  );
  if (compatibilityError) {
    useFeedbackStore.getState().showFeedback(compatibilityError, "error");
    logger.warn('generate', 'Send blocked by file/model compatibility check', {
      chat_id,
      model_id: selectedModel?.id,
    });
    return false;
  }

  // The conversation only moves on after submission preflight succeeds.
  if (clearAllApprovals) {
    clearAllApprovals();
  }

  // Initialize loading state handlers
  const loadingHandlers = createMessageLoadingHandlers({
    setIsLatestMessageLoading,
    setGenerateStatus,
    setRegenerateIndex,
    chat_id,
  });

  // Delay setChatModel to ensure chat document is created first
  if (explicitModelId && messages.length === 0 && !agent?.name) {
    const modelInfoKey = chatModelInfoQueryKey(chat_id, space?.space_id);
    if (queryClient?.setQueryData) {
      queryClient.setQueryData(modelInfoKey, (old) =>
        old
          ? {
              ...old,
              model_id: explicitModelId,
              persisted_model_id: explicitModelId,
            }
          : old
      );
    }

    // Use async function with retry logic to ensure chat model is set
    const setChatModelWithRetry = async (retries = 3, baseDelay = 1500) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Wait before attempting to set the model (increasing delay for each attempt)
          const currentDelay = baseDelay * attempt;
          await new Promise(resolve => setTimeout(resolve, currentDelay));

          await setChatModel({
            model_id: explicitModelId,
            chat_id: chat_id,
            user_id: user?.uid,
            owner_id: space?.owner_id || chat?.owner_id || user?.uid,
            org_id: orgId,
            space_id: space?.space_id || null,
          });

          if (queryClient?.setQueryData) {
            queryClient.setQueryData(modelInfoKey, (old) =>
              old
                ? {
                    ...old,
                    model_id: explicitModelId,
                    persisted_model_id: explicitModelId,
                  }
                : old
            );
          }
          if (queryClient?.invalidateQueries) {
            queryClient.invalidateQueries({ queryKey: modelInfoKey });
          }

          return;
        } catch (error) {
          if (attempt === retries) {
            logger.error('generate', 'Failed to set chat model after retries', { error: error.message, chat_id });
          }
        }
      }
    };

    // Execute the retry logic without blocking the main flow
    setChatModelWithRetry();
  }

  const { prompt, setPrompt } = PromptStore.getState();
  // Capture activeSkills synchronously before any awaits — PromptStore will be
  // cleared by Prompt.js immediately after send() returns (no-await call site),
  // so we must snapshot it here or it will be empty when buildGenerateRequestBody runs.
  const activeSkills = PromptStore.getState().activeSkills || [];

  // Guard: if any file is still uploading (fileLoading=true), bail out.
  // The send button is already disabled while fileLoading is true, but this
  // catches any race where send is triggered programmatically before all
  // uploads have resolved their downloadURL.
  if (FileStore.getState().fileLoading) {
    logger.warn('generate', 'Send called while files are still uploading — aborting');
    return false;
  }

  const filesToBeUploaded = FileStore.getState().files;

  const incompleteFiles = filesToBeUploaded.filter((file) => !file?.asset_id);
  if (incompleteFiles.length > 0) {
    logger.warn('generate', 'Send called with incomplete attachments — aborting', {
      files: incompleteFiles.map((file) => file?.name),
    });
    useFeedbackStore.getState().showFeedback(
      'One or more attachments did not finish uploading. Remove them or upload them again before sending.',
      'error'
    );
    return false;
  }

  const promptId = uuidv4();

  // Canonical asset IDs are the backend contract; display URLs are not required.
  const assetIds = filesToBeUploaded
    .map((file) => file.asset_id);
  const optimisticAttachments = filesToBeUploaded
    .filter((file) => file?.asset_id)
    .map((file) => ({
      asset_id: file.asset_id,
      filename: file.name,
      mime_type: file.mime_type,
      size_bytes: file.size,
    }));

  // Optimistic update: Create user message immediately (always predictable).
  // model_id is intentionally omitted — MODEL_USED SSE event from the backend
  // is the sole source of truth and patches the cache when streaming starts.
  sendMessageMutation.mutate({
    promptId,
    prompt,
    assetIds,
    attachments: optimisticAttachments,
    userDetails: {
      name: user?.displayName || user?.name,
      email: user?.email,
      picture: user?.photoURL || user?.picture,
      user_id: user?.uid,
    },
  });
  
  // Show loader for answer (backend will decide: stream OR approval card)
  loadingHandlers.startLoading(promptId, messages.length + 1);
  setPrompt("");
  setAddChatDisabled(false);
  
  // Optimistic update: Add new chat to sidebar immediately for first message.
  // Deduplicates by uid to avoid a second entry if invalidateQueries already
  // returned the "New Chat" doc from Firestore before this runs.
  // Applies to agent chats too — addChat skips invalidateQueries for agents so
  // without this they'd show "New Chat" until generate_chat_title fires.
  if (queryClient) {
    const queryKey = space?.space_id 
      ? ['spectra-sparks', space.space_id, orgId, user?.uid, space.owner_id]
      : [QUERYKEYS.CHATS, orgId, user?.uid];

    // Helper: move/insert a chat entry to the top position after pinned items.
    const bubbleToTop = (list, entry) => {
      const filtered = list.filter(c => c.uid !== chat_id);
      const firstUnpinnedIdx = filtered.findIndex(c => !c.pinned);
      const insertIdx = firstUnpinnedIdx === -1 ? filtered.length : firstUnpinnedIdx;
      return [...filtered.slice(0, insertIdx), entry, ...filtered.slice(insertIdx)];
    };

    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;

      let entry;
      if (messages.length === 0) {
        // New chat — build a skeleton entry from available data
        entry = {
          uid: chat_id,
          title: prompt.substring(0, 50) || 'New Spark',
          pinned: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          owner_id: user?.uid,
        };
      } else {
        // Existing chat — find and bump its updated_at so order matches backend
        const sparks = oldData?.sparks && Array.isArray(oldData.sparks) ? oldData.sparks : (Array.isArray(oldData) ? oldData : []);
        const existing = sparks.find(c => c.uid === chat_id);
        if (!existing) return oldData; // not in cache yet, nothing to reorder
        entry = { ...existing, updated_at: new Date().toISOString() };
      }

      // Handle spectra-sparks nested structure
      if (oldData.sparks && Array.isArray(oldData.sparks)) {
        return { ...oldData, sparks: bubbleToTop(oldData.sparks, entry) };
      }

      // Handle regular chats array
      if (Array.isArray(oldData)) {
        return bubbleToTop(oldData, entry);
      }

      return oldData;
    });
  }

  try {
    // Use unified API
    const reader = await generateMessageApi({
      operation: 'new',
      prompt,
      chatId: chat_id,
      promptId,
      apiUrl,
      space,
      chat,
      user,
      orgId,
      modeRef,
      modelId: explicitModelId || selectedModel?.id,
      modelOverride: Boolean(explicitModelId),
      assetIds,
      messages,
      activeSkills,
      deviceId,
      previousPromptId: lineage.previousPromptId,
      parentAnswerId: lineage.parentAnswerId,
    });

    // Use shared message-stream wiring: chunkBuffer + approval gating +
    // GENERATION_COMPLETE cleanup + ADK-stillPending cleanup gating.
    await runMessageStream({
      reader,
      messageId: promptId,
      loadingHandlers,
      // ADK early-ack: the initial POST stream closes immediately after
      // GENERATION_STARTED while the agent keeps running on the backend.
      // With this true, onComplete skips cleanup when pendingGenerations still
      // has an entry for this message — the Firestore handleAddMessage fires
      // removeSignal when the answer is persisted, naturally resolving the UI.
      // Safe for non-ADK paths: GENERATION_COMPLETE calls completeStreaming which
      // clears pendingGenerations before onComplete fires, so stillPending=false
      // and cleanup runs normally.
      checkStillPending: true,
      onPendingStreamEnd: ({ generationId, afterSeq }) => {
        const detachRecovery = attachRecoveryStream({
          generationId,
          messageId: promptId,
          afterSeq,
          apiUrl,
          queryClient,
          chat_id,
          space_id: space?.space_id,
          setIsLatestMessageLoading,
          setGenerateStatus,
        });
        const recoveryController = new AbortController();
        recoveryController.signal.addEventListener('abort', detachRecovery, { once: true });
        useGenerateApiSignal.getState().addSignal(chat_id, { signal: recoveryController });
      },
      writeChunk: (content) =>
        updateGenerateMessage(queryClient, chat_id, space?.space_id, promptId, content),
      writeModel: (modelId) =>
        updateMessageModelId(queryClient, chat_id, space?.space_id, promptId, modelId),
      onUsage: (data) =>
        updateAnswerUsage(queryClient, chat_id, space?.space_id, promptId, data.answer_id, data),
      onApprovalCard,
    });
  } catch (error) {
    const handled = loadingHandlers.handleError(error);

    if (LINEAGE_ERROR_CODES.has(error.code)) {
      queryClient?.setQueryData(
        [QUERYKEYS.CHAT_MESSAGES, chat_id, space?.space_id],
        (oldData) => oldData ? {
          ...oldData,
          messages: (oldData.messages || []).filter((message) => message.id !== promptId),
        } : oldData
      );
      setPrompt(prompt);
      useFeedbackStore.getState().showFeedback(error.message, "error");
      return false;
    }
    
    if (!handled) {
      addGenerateErrorMessage(
        queryClient,
        chat_id,
        space?.space_id,
        promptId,
        "\n" + (error.message || "Something went wrong while generating the response. Please try again.")
      );
    }
  }

  return true;
};
